import type {
  DialogAnimation,
  DialogBreakpoint,
  DialogCloseButtonVariant,
  DialogResponsive,
  DialogResponsiveClassMap,
} from "../../types/dialog";

import { cva } from "class-variance-authority";

const DIALOG_BREAKPOINTS: DialogBreakpoint[] = ["base", "sm", "md", "lg", "xl"];

/**
 * Panel enter/exit classes per `animation` token. `none` (the default) is
 * empty — the panel appears instantly, with none of Radix's `data-state`
 * classes attached, so a caller composing `className` never has to fight a
 * baked-in animation the way the old always-on zoom+fade did.
 *
 * The overlay only ever needs the `fade` pair regardless of the panel's own
 * token — see DialogContent's `animation !== "none"` check — so this map
 * covers the panel alone.
 */
const DIALOG_ANIMATION_CLASSES: Record<DialogAnimation, string> = {
  fade: "data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
  none: "",
  scale:
    "data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
  "slide-up":
    "data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:slide-out-to-bottom-full data-[state=open]:slide-in-from-bottom-full",
};

const DIALOG_WIDTH_CLASSES: DialogResponsiveClassMap = {
  base: {
    full: "w-full",
    lg: "w-[40rem]",
    md: "w-[30rem]",
    sm: "w-[25rem]",
    xl: "w-[50rem]",
    xs: "w-[20rem]",
  },
  lg: {
    full: "lg:w-full",
    lg: "lg:w-[40rem]",
    md: "lg:w-[30rem]",
    sm: "lg:w-[25rem]",
    xl: "lg:w-[50rem]",
    xs: "lg:w-[20rem]",
  },
  md: {
    full: "md:w-full",
    lg: "md:w-[40rem]",
    md: "md:w-[30rem]",
    sm: "md:w-[25rem]",
    xl: "md:w-[50rem]",
    xs: "md:w-[20rem]",
  },
  sm: {
    full: "sm:w-full",
    lg: "sm:w-[40rem]",
    md: "sm:w-[30rem]",
    sm: "sm:w-[25rem]",
    xl: "sm:w-[50rem]",
    xs: "sm:w-[20rem]",
  },
  xl: {
    full: "xl:w-full",
    lg: "xl:w-[40rem]",
    md: "xl:w-[30rem]",
    sm: "xl:w-[25rem]",
    xl: "xl:w-[50rem]",
    xs: "xl:w-[20rem]",
  },
};

const DIALOG_HEIGHT_CLASSES: DialogResponsiveClassMap = {
  base: {
    auto: "h-auto",
    full: "h-[100dvh]",
    lg: "h-[40rem]",
    md: "h-[30rem]",
    sm: "h-[20rem]",
  },
  lg: {
    auto: "lg:h-auto",
    full: "lg:h-[100dvh]",
    lg: "lg:h-[40rem]",
    md: "lg:h-[30rem]",
    sm: "lg:h-[20rem]",
  },
  md: {
    auto: "md:h-auto",
    full: "md:h-[100dvh]",
    lg: "md:h-[40rem]",
    md: "md:h-[30rem]",
    sm: "md:h-[20rem]",
  },
  sm: {
    auto: "sm:h-auto",
    full: "sm:h-[100dvh]",
    lg: "sm:h-[40rem]",
    md: "sm:h-[30rem]",
    sm: "sm:h-[20rem]",
  },
  xl: {
    auto: "xl:h-auto",
    full: "xl:h-[100dvh]",
    lg: "xl:h-[40rem]",
    md: "xl:h-[30rem]",
    sm: "xl:h-[20rem]",
  },
};

/**
 * Fullscreen drops the backdrop entirely; the panel covers the viewport, so
 * there is nothing left to dim.
 */
const DIALOG_OVERLAY_FULLSCREEN_CLASSES: DialogResponsiveClassMap = {
  base: { false: "bg-black/40", true: "bg-transparent" },
  lg: { false: "lg:bg-black/40", true: "lg:bg-transparent" },
  md: { false: "md:bg-black/40", true: "md:bg-transparent" },
  sm: { false: "sm:bg-black/40", true: "sm:bg-transparent" },
  xl: { false: "xl:bg-black/40", true: "xl:bg-transparent" },
};

/** Fullscreen removes the margin between the panel and the viewport edge. */
const DIALOG_VIEWPORT_FULLSCREEN_CLASSES: DialogResponsiveClassMap = {
  base: { false: "p-4", true: "p-0" },
  lg: { false: "lg:p-4", true: "lg:p-0" },
  md: { false: "md:p-4", true: "md:p-0" },
  sm: { false: "sm:p-4", true: "sm:p-0" },
  xl: { false: "xl:p-4", true: "xl:p-0" },
};

/**
 * The `false` sets deliberately omit width and height so they never clobber the
 * `width`/`height` props when a smaller breakpoint is fullscreen.
 */
const DIALOG_CONTENT_FULLSCREEN_CLASSES: DialogResponsiveClassMap = {
  base: {
    false:
      "max-h-[calc(100dvh-2rem)] max-w-[calc(100vw-2rem)] rounded-3xl shadow-xl",
    true: "h-[100dvh] max-h-none w-screen max-w-none rounded-none shadow-none",
  },
  lg: {
    false:
      "lg:max-h-[calc(100dvh-2rem)] lg:max-w-[calc(100vw-2rem)] lg:rounded-3xl lg:shadow-xl",
    true: "lg:h-[100dvh] lg:max-h-none lg:w-screen lg:max-w-none lg:rounded-none lg:shadow-none",
  },
  md: {
    false:
      "md:max-h-[calc(100dvh-2rem)] md:max-w-[calc(100vw-2rem)] md:rounded-3xl md:shadow-xl",
    true: "md:h-[100dvh] md:max-h-none md:w-screen md:max-w-none md:rounded-none md:shadow-none",
  },
  sm: {
    false:
      "sm:max-h-[calc(100dvh-2rem)] sm:max-w-[calc(100vw-2rem)] sm:rounded-3xl sm:shadow-xl",
    true: "sm:h-[100dvh] sm:max-h-none sm:w-screen sm:max-w-none sm:rounded-none sm:shadow-none",
  },
  xl: {
    false:
      "xl:max-h-[calc(100dvh-2rem)] xl:max-w-[calc(100vw-2rem)] xl:rounded-3xl xl:shadow-xl",
    true: "xl:h-[100dvh] xl:max-h-none xl:w-screen xl:max-w-none xl:rounded-none xl:shadow-none",
  },
};

const dialogCloseButtonVariants = cva(
  "absolute top-4 right-4 z-10 inline-flex aspect-square size-11 min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-new-beginnings-base focus-visible:outline-offset-2",
  {
    defaultVariants: {
      variant: "outline",
    },
    variants: {
      variant: {
        ghost:
          "bg-transparent text-new-beginnings-base hover:bg-new-beginnings-tints-800",
        outline:
          "border-2 border-new-beginnings-base bg-transparent text-new-beginnings-base hover:bg-new-beginnings-base hover:text-support-base",
        solid:
          "bg-new-beginnings-base text-support-base hover:bg-new-beginnings-tints-800 hover:text-new-beginnings-base",
      } satisfies Record<DialogCloseButtonVariant, string>,
    },
  },
);

function normalizeResponsiveValue<TValue>(
  value: DialogResponsive<TValue> | undefined,
): Partial<Record<DialogBreakpoint, TValue>> {
  if (value === undefined) {
    return {};
  }

  if (typeof value === "object" && value !== null) {
    return value as Partial<Record<DialogBreakpoint, TValue>>;
  }

  return { base: value };
}

/**
 * Turns a responsive prop into the literal Tailwind classes for each supplied
 * breakpoint, smallest first so `twMerge` resolves conflicts predictably.
 */
function resolveResponsiveClasses<TValue extends string | boolean>(
  value: DialogResponsive<TValue> | undefined,
  classesByBreakpoint: DialogResponsiveClassMap,
): string[] {
  const valueByBreakpoint = normalizeResponsiveValue(value);
  const resolved: string[] = [];

  for (const breakpoint of DIALOG_BREAKPOINTS) {
    const token = valueByBreakpoint[breakpoint];

    if (token === undefined) {
      continue;
    }

    const className = classesByBreakpoint[breakpoint][String(token)];

    if (className) {
      resolved.push(className);
    }
  }

  return resolved;
}

export {
  DIALOG_ANIMATION_CLASSES,
  DIALOG_CONTENT_FULLSCREEN_CLASSES,
  DIALOG_HEIGHT_CLASSES,
  DIALOG_OVERLAY_FULLSCREEN_CLASSES,
  DIALOG_VIEWPORT_FULLSCREEN_CLASSES,
  DIALOG_WIDTH_CLASSES,
  dialogCloseButtonVariants,
  resolveResponsiveClasses,
};
